/**
 * The thing that wakes the tab at the roll's five instants.
 *
 * `domain/roll.ts` answers *when*; this sleeps until then and says *now*. The
 * decision `docs/10` §4.3 left to the implementer was whether these live on
 * `Runtime` as four more timers or in a class `Runtime` owns — a class, because
 * `Runtime` already held `#scheduleAnchorFollow` and five independent schedules
 * on one grid is a scheduler whether or not it is named one.
 *
 * ## The `RotationScheduler` lessons, all three (§F21m)
 *
 * **Sleep to the next window, never poll.** The events are 8 to 42 seconds
 * apart; an interval short enough to land on each of them wakes fifty times a
 * minute to do nothing forty-five of them.
 *
 * **Re-derive from the corrected clock on every tick, never run as an
 * interval.** An interval drifts against the server, and the whole point is to
 * turn over when the *server* says the anchor did. `Runtime#scheduleAnchorFollow`
 * already had this property and it is kept.
 *
 * **Re-derive when the state underneath changes.** `reschedule()` is that, and
 * the roll has four such changes: an asset switch, a market that resolves early,
 * one that goes `VOIDED` at ~`T+80`, and an N+1 that is `CANCELLED`. The first
 * calls `reschedule()` directly; the other three are absorbed rather than
 * scheduled around — the watchdog re-reads the market's state at the instant it
 * fires and does nothing if it has resolved, and a cancelled successor changes
 * what renders rather than when anything happens.
 *
 * ## Why the fired-set exists
 *
 * A wake-up is not "the event"; it is "at least one event is due". A tab that
 * was backgrounded for two minutes wakes once with four events past due, and a
 * scheduler that fired only the earliest would take four wake-ups to catch up.
 * So each tick fires **every** due event that has not fired for its own market,
 * keyed by `kind:marketId`, which makes each one idempotent and makes catch-up
 * one pass.
 *
 * Past-due events on a cold load are *skipped*, not fired, for everything except
 * the swap: a tab that boots at `T+45` never held the predecessor's channels, so
 * tearing them down is a no-op, and `Runtime` seeds the holds declaratively at
 * boot instead. The swap is different — it is the anchor follow, and a tab whose
 * clock correction lands it in the next market must re-point immediately.
 *
 * No React, no DOM. `Runtime` supplies the clock and does the work.
 */

import {
  rollSchedule,
  type RollEvent,
  type RollEventKind,
  type RollJitter,
  type RollTiming,
} from '@/domain/roll'

export interface RollCycle {
  /** The anchor of the market currently on screen, in **milliseconds**. */
  anchorMs: number
  timing: RollTiming
}

export interface RollSchedulerOptions {
  /** Corrected server time. Never `Date.now()` — invariant 7. */
  now: () => number
  /** `null` while the runtime is not `READY`; the scheduler then idles. */
  getCycle: () => RollCycle | null
  /** The predecessor's `resolve_eta_ms`, read from its payload (invariant 11). */
  resolveEtaFor: (marketId: string) => number | null
  /** `market_id` of the market that expired at the current anchor. */
  predecessorId: () => string | null
  /** `market_id` of the market that opens at the next anchor. */
  successorId: () => string | null
  jitter: RollJitter
  /**
   * `atMs` is the event's *scheduled* instant, not the wake-up's.
   *
   * The caller derives its subscription set from a clock comparison against the
   * same five instants, so it has to evaluate at a time that is provably at or
   * past the one that woke it. Handing it `clock.now()` instead would let a
   * backwards clock correction between scheduling and firing produce a teardown
   * that decides it is not due yet and then never wakes again.
   */
  onEvent: (kind: RollEventKind, marketId: string, atMs: number) => void
  setTimeout?: (fn: () => void, ms: number) => number
  clearTimeout?: (handle: number) => void
}

/** Never sleep less than this — a zero-delay timer spins. */
const MIN_DELAY_MS = 20
/** Nothing to schedule (no cycle yet). Long, because the caller nudges us. */
const IDLE_RECHECK_MS = 5_000
/** How far past its instant an event may still fire. Beyond this the tab was
 *  asleep or throttled and the event is stale rather than late. */
const DUE_WINDOW_MS = 5_000
/** Bounds the fired-set. Five kinds × a handful of markets; 64 is a whole hour
 *  of headroom and keeps a tab open all day from growing a set. */
const FIRED_CAPACITY = 64

/**
 * Everything but the swap.
 *
 * The other four are all recoverable without firing: three of them only
 * rearrange subscriptions, and `Runtime#applyHolds` derives the right set from
 * the clock whenever it is asked, so a late one would ask for an answer that is
 * already correct. The watchdog is the important entry — firing it late is an
 * HTTP request about a market that settled while the tab was asleep, and the
 * roll's whole budget is one request on the *unhappy* path and zero on the
 * happy one.
 *
 * The swap is not in here because it is the anchor follow: a tab that wakes two
 * minutes late is rendering a dead market until it fires.
 */
const SKIP_WHEN_PAST_DUE: ReadonlySet<RollEventKind> = new Set<RollEventKind>([
  'TEARDOWN',
  'RETIRE',
  'PREWARM',
  'WATCHDOG',
])

export class RollScheduler {
  readonly #options: RollSchedulerOptions
  readonly #setTimeout: (fn: () => void, ms: number) => number
  readonly #clearTimeout: (handle: number) => void
  #handle: number | null = null
  #running = false
  /** `kind:marketId` of everything already fired, so a tick is idempotent. */
  #fired = new Set<string>()

  constructor(options: RollSchedulerOptions) {
    this.#options = options
    this.#setTimeout =
      options.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number)
    this.#clearTimeout = options.clearTimeout ?? ((handle) => globalThis.clearTimeout(handle))
  }

  get running(): boolean {
    return this.#running
  }

  start(): void {
    if (this.#running) return
    this.#running = true
    this.tick()
  }

  stop(): void {
    this.#running = false
    if (this.#handle !== null) this.#clearTimeout(this.#handle)
    this.#handle = null
  }

  /**
   * Recompute now, discarding the sleep in flight — §F21m's third lesson.
   *
   * Called when the state the schedule is derived from changes underneath it:
   * today that is the asset switch, which changes both the predecessor whose
   * `resolve_eta_ms` sets the watchdog's instant and the market ids the
   * fired-set is keyed by.
   *
   * It does **not** replay the events already past in this cycle, and it does
   * not need to. Switching asset at `T+50` must still pre-warm the arriving
   * asset's successor — but `Runtime#applyHolds` derives the subscription set
   * from the clock rather than accumulating it from events, so the switch has
   * already produced the same answer the pre-warm would have. Replaying would
   * be a second way to reach a state the first way is already in.
   */
  reschedule(): void {
    if (!this.#running) return
    this.tick()
  }

  /**
   * One evaluation: fire what is due, then sleep to the next thing that is not.
   *
   * Exposed so tests can step it without a fake timer — the same shape
   * `RotationScheduler.tick()` has, and for the same reason.
   */
  tick(): void {
    if (!this.#running) return
    const cycle = this.#options.getCycle()
    if (!cycle) return this.#sleep(IDLE_RECHECK_MS)

    const nowMs = this.#options.now()
    const predecessor = this.#options.predecessorId()
    const successor = this.#options.successorId()
    const events = rollSchedule(
      cycle.anchorMs,
      cycle.timing,
      this.#options.jitter,
      predecessor === null ? null : this.#options.resolveEtaFor(predecessor),
    )

    let next: number | null = null
    for (const event of events) {
      const marketId = this.#marketFor(event, predecessor, successor)
      if (marketId === null) continue
      if (event.atMs > nowMs) {
        if (next === null || event.atMs < next) next = event.atMs
        continue
      }
      const key = `${event.kind}:${marketId}`
      if (this.#fired.has(key)) continue
      this.#remember(key)
      // Stale rather than late: a backgrounded tab wakes with a teardown from
      // three markets ago, and performing it would unsubscribe channels the
      // boot path has already re-derived correctly.
      if (nowMs - event.atMs > DUE_WINDOW_MS && SKIP_WHEN_PAST_DUE.has(event.kind)) continue
      this.#options.onEvent(event.kind, marketId, event.atMs)
    }

    // The swap re-points the anchor, so everything after it belongs to the next
    // cycle and has to be derived from the new one. One extra pass, once a
    // minute, and it is what makes `SWAP` the only event that needs no special
    // case in `Runtime`.
    const cycleAfter = this.#options.getCycle()
    if (cycleAfter && cycleAfter.anchorMs !== cycle.anchorMs) return this.tick()

    this.#sleep(next === null ? IDLE_RECHECK_MS : Math.max(MIN_DELAY_MS, next - nowMs))
  }

  /** Which market an event is *about*. The first three are the predecessor's,
   *  the last two the successor's. */
  #marketFor(event: RollEvent, predecessor: string | null, successor: string | null): string | null {
    switch (event.kind) {
      case 'TEARDOWN':
      case 'RETIRE':
      case 'WATCHDOG':
        return predecessor
      case 'PREWARM':
      case 'SWAP':
        return successor
    }
    return null
  }

  #remember(key: string): void {
    this.#fired.add(key)
    if (this.#fired.size <= FIRED_CAPACITY) return
    // Insertion order, which for these keys is time order.
    const oldest = this.#fired.values().next().value
    if (oldest !== undefined) this.#fired.delete(oldest)
  }

  #sleep(ms: number): void {
    if (!this.#running) return
    if (this.#handle !== null) this.#clearTimeout(this.#handle)
    this.#handle = this.#setTimeout(() => this.tick(), ms)
  }
}
